<!--
*  TTTech nerve-ui-components
*  Copyright(c) 2021. TTTech Industrial Automation AG.
*
*  ALL RIGHTS RESERVED.
*
*  Usage of this software, including source code, netlists, documentation,
*  is subject to restrictions and conditions of the applicable license
*  agreement with TTTech Industrial Automation AG or its affiliates.
*
*  All trademarks used are the property of their respective owners.
*
*  TTTech Industrial Automation AG and its affiliates do not assume any liability
*  arising out of the application or use of any product described or shown
*  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
*  make changes, at any time, in order to improve reliability, function or
*  design.
*
*  Contact Information:
*  support@tttech-industrial.com
*
*  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
*
* -->
<template>
  <div>
    <span class="font-size-18">
      {{ $t('vmSnapshot.snapshotTitle') }}
    </span>
    <v-col cols="10" class="pl-0">
      <the-nerve-table
        :columns="columns"
        :empty-state="$t('vmSnapshot.emptyState')"
        :is-action-menu-enabled="false"
        :is-row-clickable="false"
        :hide-footer="true"
        :disable-pagination="true"
        :is-search-enabled="false"
        :delete-message="$t('vmSnapshot.removeSnapshot')"
        :store-module="storeModule"
        :custom-list-getter="customListGetter"
        :params="params"
        @tableAction="(item) => $emit('restoreSnapshot', item)"
      />
    </v-col>
    <v-col lg="3" md="5" sm="8" xs="12" cols="12" class="pl-0 mt-3 mb-12">
      <v-card
        v-show="savedSchedule && (savedSchedule.type === 'Interval' || savedSchedule.type === 'Day')"
        class="card-color"
        elevation="2"
      >
        <v-row>
          <v-col cols="10" class="pt-6 pb-6 pl-8 font-size-14">
            <v-row>
              {{ $t('vmSnapshot.scheduleCardTitle') }}
            </v-row>
            <v-row v-if="savedSchedule && savedSchedule.type && savedSchedule.type === 'Interval'">
              <span class="opacity-5">
                {{ $t('vmSnapshot.scheduledType') }}
              </span>
              <span class="ml-1">
                {{ savedSchedule.type }}
              </span>
              <span class="opacity-5 ml-3">
                {{ $t('vmSnapshot.scheduledDayHours') }}
              </span>
              <span class="ml-1">
                {{ intervalHoursCard }}
              </span>
            </v-row>
            <v-row v-if="savedSchedule && savedSchedule.type && savedSchedule.type === 'Day'">
              <span class="opacity-5">
                {{ $t('vmSnapshot.scheduledType') }}
              </span>
              <span class="ml-1">
                {{ savedSchedule.type }}
              </span>
              <span class="opacity-5 ml-3 mr-1">
                {{ $t('vmSnapshot.scheduledDay') }}
              </span>
              <span class="mr-3">
                {{ savedSchedule.day }}
              </span>
            </v-row>
            <v-row v-if="savedSchedule && savedSchedule.type && savedSchedule.type === 'Day'">
              <span class="opacity-5 mr-1">
                {{ $t('vmSnapshot.scheduledDayHours') }}
              </span>
              <span>
                {{ dayHoursCard }}
              </span>
            </v-row>
          </v-col>
          <v-col v-if="editSnapshots" cols="2" class="x-button-position">
            <v-icon id="iiotVmSnapshotDeleteScheduleButton" class="pointer" @click="deleteSchedule()">
              mdi-close
            </v-icon>
          </v-col>
        </v-row>
      </v-card>
    </v-col>
    <v-row v-if="editSnapshots" class="mt-3">
      <v-divider />
    </v-row>
    <v-form v-if="editSnapshots" ref="snapshotForm" class="pa-0" @submit.prevent="saveSnapshot">
      <v-row v-resize="onResize">
        <v-col lg="3" md="5" sm="8" xs="12" cols="12" class="mt-9">
          <span class="font-size-18">{{ $t('vmSnapshot.create') }}</span>
          <v-text-field
            id="iiotVmSnapshotAddDialogName"
            v-model="newSnapshot.name"
            :label="$t('vmSnapshot.dialogName')"
            :rules="[rules.required, rules.name_contains_only_spaces, rules.name_contains_control_character]"
            :disabled="snapshot && snapshot.length > 0"
            class="mt-5"
            validate-on-blur
            @focus="generateSnapshotName"
          />
          <v-textarea
            id="iiotVmSnapshotAddDialogDescription"
            v-model="newSnapshot.description"
            :label="$t('vmSnapshot.dialogDescription')"
            :disabled="snapshot && snapshot.length > 0"
            class="mt-6"
            outlined
            validate-on-blur
          />
          <nerve-button
            id="iiotVmSnapshotAddDialogCreateButton"
            :text="$t('vmSnapshot.snapshotDialogCreateButton')"
            :disabled="snapshot && snapshot.length > 0"
            type-of-btn="action"
            size="normal"
            autofocus
            type="submit"
          />
        </v-col>
        <div v-if="isDividerVisible" class="ml-8">
          <v-divider vertical class="ml-16 mt-5 mr-8" />
        </div>
        <v-col lg="4" md="8" sm="10" xs="12" cols="12" class="mt-9">
          <span class="font-size-18">
            {{ $t('vmSnapshot.schedule') }}
          </span>
          <v-form ref="scheduleForm" class="pa-0">
            <v-col id="iiotVmSnapshotScheduleTypeDiv" cols="5" class="pl-0">
              <v-select
                id="iiotVmSnapshotScheduleType"
                v-model="schedule.type"
                :items="items"
                :label="$t('vmSnapshot.scheduleType')"
                :disabled="savedSchedule && (savedSchedule.type === 'Interval' || savedSchedule.type === 'Day')"
                @change="changeSchedule()"
              />
            </v-col>
            <v-row>
              <v-col v-if="schedule.type === 'Interval'" cols="2" class="pt-0">
                <v-text-field
                  id="iiotVmSnapshotIntervalTime"
                  v-model.number="schedule.intervalTime"
                  :label="$t('vmSnapshot.intervalTime')"
                  :disabled="savedSchedule && (savedSchedule.type === 'Interval' || savedSchedule.type === 'Day')"
                  :max="maxScheduleInterval"
                  :min="minScheduleInterval"
                  oninput="if(Number(this.value) > Number(this.max)) this.value = this.max;
                  if(Number(this.value) < Number(this.min)) this.value = this.min;"
                  type="number"
                />
              </v-col>
              <v-col v-if="schedule.type === 'Day'" id="iiotVmSnapshotDayDiv" cols="5" class="ml-3">
                <v-row>
                  <v-select
                    id="iiotVmSnapshotDay"
                    v-model="schedule.day"
                    :items="days"
                    :rules="[rules.selectFromListType]"
                    :disabled="savedSchedule && (savedSchedule.type === 'Interval' || savedSchedule.type === 'Day')"
                    :label="$t('vmSnapshot.day')"
                    class="pr-5"
                  />
                </v-row>
              </v-col>
              <v-row>
                <v-col v-if="schedule.type === 'Day'" cols="2" class="ml-3">
                  <v-text-field
                    id="iiotVmSnapshotDayHours"
                    v-model.number="schedule.dayHours"
                    :label="$t('vmSnapshot.dayHours')"
                    :disabled="savedSchedule && (savedSchedule.type === 'Interval' || savedSchedule.type === 'Day')"
                    :max="maxHours"
                    :min="minHours"
                    oninput="if(Number(this.value) > Number(this.max)) this.value = this.max;
                    if(Number(this.value) === 0) this.value = this.min;"
                    type="number"
                    onkeypress="return event.charCode >= 48 && event.charCode <= 57"
                  />
                </v-col>
                <v-col v-if="schedule.type === 'Day'" cols="2">
                  <v-text-field
                    id="iiotVmSnapshotDayMinutes"
                    v-model.number="schedule.dayMinutes"
                    :label="$t('vmSnapshot.dayMinutes')"
                    :disabled="savedSchedule && (savedSchedule.type === 'Interval' || savedSchedule.type === 'Day')"
                    :max="maxMinutes"
                    :min="minMinutes"
                    oninput="if(Number(this.value) > Number(this.max)) this.value = this.max;
                    if(Number(this.value) === 0) this.value = this.min;"
                    type="number"
                    onkeypress="return event.charCode >= 48 && event.charCode <= 57"
                  />
                </v-col>
                <v-col
                  v-if="schedule.type != '' && schedule.type != null && schedule.type != undefined"
                  id="iiotVmSnapshotVmStateDiv"
                  cols="4"
                >
                  <v-select
                    id="iiotVmSnapshotVmState"
                    v-model="schedule.vmState"
                    :items="scheduleVmStateList"
                    :rules="[rules.selectFromListType]"
                    :disabled="savedSchedule && (savedSchedule.type === 'Interval' || savedSchedule.type === 'Day')"
                    :label="$t('vmSnapshot.scheduleVmState')"
                  />
                </v-col>
              </v-row>
            </v-row>
            <v-col v-if="schedule.type !== ''" cols="8" class="warning-color pl-0 mb-5">
              {{ $t('vmSnapshot.scheduleMessage') }}
            </v-col>
            <v-row v-if="schedule.type !== ''">
              <nerve-button
                id="iiotVmSnapshotSaveScheduleButton"
                :text="$t('vmSnapshot.saveSchedule')"
                :disabled="savedSchedule && (savedSchedule.type === 'Interval' || savedSchedule.type === 'Day')"
                type-of-btn="action"
                size="normal"
                class="ml-3 mb-4"
                @click-event="saveSchedule()"
              />
            </v-row>
          </v-form>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import TheNerveTable from './TheNerveTable.vue';
import NerveButton from './NerveButton.vue';
import { VALIDATION_REGEX } from './helpers/constants';

export default {
  name: 'VmSnapshot',
  components: {
    TheNerveTable,
    NerveButton,
  },
  props: {
    isOnlyOneElement: {
      type: Boolean,
      default: true,
    },
    storeModule: {
      type: String,
      default: '',
    },
    customListGetter: {
      type: String,
      default: '',
    },
    params: {
      type: Object,
      default: () => {},
    },
    savedSchedule: {
      type: Object,
      default: () => {},
    },
    workloadId: {
      type: String,
      default: '',
    },
    versionId: {
      type: String,
      default: '',
    },
    editSnapshots: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      items: [
        { value: 'Interval', text: this.$t('vmSnapshot.scheduleInterval') },
        { value: 'Day', text: this.$t('vmSnapshot.scheduleDay') },
      ],
      days: [
        { value: 'Monday', text: this.$t('vmSnapshot.scheduleDayMonday') },
        { value: 'Tuesday', text: this.$t('vmSnapshot.scheduleDayTuesday') },
        {
          value: 'Wednesday',
          text: this.$t('vmSnapshot.scheduleDayWednesday'),
        },
        { value: 'Thursday', text: this.$t('vmSnapshot.scheduleDayThursday') },
        { value: 'Friday', text: this.$t('vmSnapshot.scheduleDayFriday') },
        { value: 'Saturday', text: this.$t('vmSnapshot.scheduleDaySaturday') },
        { value: 'Sunday', text: this.$t('vmSnapshot.scheduleDaySunday') },
        { value: 'Every day', text: this.$t('vmSnapshot.scheduleDayEveryDay') },
      ],
      newSnapshot: {
        name: '',
        description: '',
      },
      scheduleVmStateList: [
        { value: 'Current', text: this.$t('vmSnapshot.scheduleCurrent') },
        { value: 'Stopped', text: this.$t('vmSnapshot.scheduleStopped') },
      ],
      maxScheduleInterval: 9999,
      minScheduleInterval: 1,
      maxHours: 23,
      minHours: 0,
      maxMinutes: 59,
      minMinutes: 0,
      isScheduleCard: false,
      schedule: {
        type: '',
      },
      isDividerVisible: true,
    };
  },
  computed: {
    rules() {
      return {
        required: (value) => !!value || this.$t('vmSnapshot.dialogRequired'),
        selectFromListType: (value) =>
          (value !== undefined && value.length > 0) || this.$t('vmSnapshot.dialogRequired'),
        name_contains_only_spaces: (value) =>
          !VALIDATION_REGEX.STRING_CONTAINS_ONLY_SPACES.test(value) || this.$t('vmSnapshot.wlNameContainOnlySpaces'),
        name_contains_control_character: (value) =>
          VALIDATION_REGEX.NO_CONTROL_CHARACTERS.test(value) || this.$t('vmSnapshot.wlNameContainControlCharacter'),
      };
    },
    columns() {
      return [
        {
          text: this.$t('vmSnapshot.name'),
          value: 'name',
          width: '30%',
          sortable: false,
          isTruncateEnabled: true,
        },
        {
          text: this.$t('vmSnapshot.date'),
          value: 'date',
          sortable: false,
        },
        {
          text: this.$t('vmSnapshot.description'),
          value: 'description',
          sortable: false,
          width: '40%',
          isTruncateEnabled: true,
        },
        ...(this.editSnapshots
          ? [
              {
                text: this.$t('defaultTable.action'),
                value: '',
                width: '0%',
                sortable: false,
                component: {
                  sfc: () => import('./helpers/VmSnapshotRestoreButton.vue'),
                  props: {
                    workloadId: this.workloadId,
                    versionId: this.versionId,
                    iconsAction: ['Restore', 'Delete'],
                  },
                },
              },
            ]
          : []),
      ];
    },
    snapshot() {
      return this.$store.getters['vm-snapshot/list'] || this.$store.getters[`vm-snapshot/${this.customListGetter}`];
    },
    intervalHoursCard() {
      return this.savedSchedule && this.savedSchedule.intervalTime
        ? `0${this.savedSchedule.intervalTime}`.slice(-2)
        : '';
    },
    dayHoursCard() {
      return this.savedSchedule && this.savedSchedule.dayTime ? this.savedSchedule.dayTime : '';
    },
  },
  methods: {
    changeSchedule() {
      this.schedule.intervalTime = 1;
      this.schedule.day = 'Monday';
      this.schedule.dayTime = '';
      this.schedule.dayHours = 1;
      this.schedule.dayMinutes = 1;
      this.schedule.vmState = 'Current';
    },
    async saveSnapshot() {
      if (!this.$refs.snapshotForm.validate()) {
        return;
      }
      this.$emit('saveSnapshot', this.newSnapshot);
      this.newSnapshot.name = '';
      this.newSnapshot.description = '';
    },
    generateSnapshotName() {
      const timeZoneInfo = this.getCurrentTimeZoneInfo();
      const current = new Date();
      const month = current.getMonth() + 1;
      this.newSnapshot.name = `Snapshot ${current.getDate()}-${month}-${current.getFullYear()} ${current.getHours()}:${current.getMinutes()}:${current.getSeconds()} GMT${
        timeZoneInfo.timezoneOffset
      }`;
    },
    deleteSchedule() {
      this.$store.dispatch('utils/_api_request_handler/show_confirm_dialog', {
        title: this.$t('vmSnapshot.deleteScheduleTitle'),
        subTitle: this.$t('vmSnapshot.deleteScheduleSubtitle'),
        callback: async () => {
          this.$emit('deleteSchedule', this.schedule);
          this.isScheduleCard = false;
        },
      });
    },
    async saveSchedule() {
      if (!this.$refs.scheduleForm.validate()) {
        return;
      }
      const timeZoneInfo = this.getCurrentTimeZoneInfo();
      this.schedule.timeZone = timeZoneInfo.timeZone;
      this.schedule.timezoneOffset = timeZoneInfo.timezoneOffset;
      if (this.schedule.type === 'Day') {
        const hours = `0${this.schedule.dayHours}`.slice(-2);
        const minutes = `0${this.schedule.dayMinutes}`.slice(-2);
        this.dayHoursCard = `${hours}:${minutes}`;
        this.schedule.dayTime = `${this.schedule.dayHours}:${minutes}`;
      }
      if (this.schedule.type === 'Interval') {
        this.intervalHoursCard = `0${this.schedule.intervalTime}`.slice(-2);
      }
      this.isScheduleCard = true;
      this.$emit('saveSchedule', this.schedule);
    },
    getCurrentTimeZoneInfo() {
      const current = new Date();
      const offset = -current.getTimezoneOffset();
      const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
      // eslint-disable-next-line radix
      const timezoneOffset = (offset >= 0 ? '+' : '-') + parseInt(offset / 60);
      return { timeZone, timezoneOffset };
    },
    onResize() {
      this.isDividerVisible = window.innerWidth > 960;
    },
  },
};
</script>
<style scoped lang="scss">
.primary-tttech-background-btn {
  background-image: linear-gradient(to bottom, var(--v-primary-base), var(--v-primary-darken1));
}
.primary-tttech-background-btn-disabled {
  background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0.12));
}
.primary-tttech-background-for-btn {
  background-image: linear-gradient(to bottom, var(--v-primary-base), var(--v-primary-darken1));
}
.tertiary-tttech-background-for-btn {
  background-image: linear-gradient(to bottom, var(--v-tertiary-lighten4), var(--v-tertiary-base));
}
.theme--dark.v-btn.v-btn--disabled .v-icon,
.theme--dark.v-btn.v-btn--disabled .v-btn__loading {
  color: rgba(0, 0, 0, 0.26) !important;
}

.x-button-position {
  display: flex;
  justify-content: center;
  align-items: center;
}

.pointer {
  cursor: pointer;
  opacity: 0.3;
}

.font-size-14 {
  font-size: 14px;
}

.opacity-5 {
  opacity: 0.5;
}
.card-color {
  background-color: rgba(251, 252, 252, 1);
}
.warning-color {
  color: #ff6100;
}
.font-size-18 {
  font-size: 18px;
}
</style>
